import { UNDO_REQUEST_ACTION_SUGGEST_PROVIDER } from "core/consts";
import { AuctionRequest, QueryProgress } from "core/types";
import {
  EventSpecsEnhanced,
  getCancelAction,
} from "dsl/atoms/EventSpecs/utils";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useCareseekerNavigationHandlers } from "dsl/hooks/useNavigationHandlers";
import { useUndoRequestAction } from "dsl/hooks/useRequestAction";
import EventCard, { EventCardProps } from "dsl/molecules/Messenger/EventCard";
import { useCallback } from "react";
import { useTranslations } from "translations";

export type EventCardCareseekerProps = {
  auctionRequest: AuctionRequest;
  detached?: boolean;
  event: EventSpecsEnhanced;
  isDividerVisible: EventCardProps["isDividerVisible"];
  print?: boolean;
  shouldShowBefore: EventCardProps["shouldShowBefore"];
};

const useOnCancelActionCareseeker = (
  auctionRequest: AuctionRequest,
): [() => Promise<void>, QueryProgress] => {
  const { patient: patientNavigationHandler } =
    useCareseekerNavigationHandlers();
  const toast = useToast();
  const translations = useTranslations();
  const [undoAction, undoActionQueryProgress, undoActionType] =
    useUndoRequestAction({
      auctionRequest,
    });
  const onCancel = useCallback(async () => {
    await undoAction({
      auctionRequest,
      onCompleted: () => {
        if (undoActionType === UNDO_REQUEST_ACTION_SUGGEST_PROVIDER) {
          patientNavigationHandler.goToSearch({
            auctionId: auctionRequest.auction_id ?? -1,
            patientId: auctionRequest.auction?.patient?.id ?? -1,
          });
        }
      },
      onError: () => {
        toast({
          color: "danger",
          message: translations.auctionRequest.tryAgain,
        });
      },
    });
  }, [auctionRequest, undoAction]);
  return [onCancel, undoActionQueryProgress];
};

export default function EventCardCareseeker({
  auctionRequest,
  detached,
  event,
  isDividerVisible,
  print,
  shouldShowBefore,
}: EventCardCareseekerProps) {
  const [onCancel, onCancelSubmitProgress] =
    useOnCancelActionCareseeker(auctionRequest);

  return (
    <EventCard
      cancelAction={getCancelAction({
        eventType: event.type,
        onCancel,
        requestActions: auctionRequest?.request_actions,
      })}
      cardFormat={{
        ...event.cardFormat,
        lastSeens: detached ? undefined : event.cardFormat.lastSeens,
      }}
      eventMessages={event.eventMessages}
      file={event.file}
      Icon={event.Icon}
      isDividerVisible={isDividerVisible}
      isEventUserInitiated={event.isEventUserInitiated}
      messages={event.messages}
      print={print}
      searchType={event.searchType}
      sentByLabel={event.sentByLabel}
      shouldShowBefore={shouldShowBefore}
      statusHeader={event.statusHeader}
      timestamp={event.timestamp}
      type={event.type}
      onSubmitQueryProgress={onCancelSubmitProgress}
    />
  );
}
